
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Check, Star, Users, MessageCircle, Crown, ArrowRight } from 'lucide-react';

const EarlyAccess = () => {
  const [isAgreed, setIsAgreed] = useState(false);
  const navigate = useNavigate();

  const handleCreateProfile = () => {
    if (isAgreed) {
      navigate('/auth');
    }
  };

  const benefits = [
    {
      icon: <Crown className="w-5 h-5 text-luxury-gold" />,
      text: "Sem fila de aprovação"
    },
    {
      icon: <ArrowRight className="w-5 h-5 text-luxury-gold" />,
      text: "Acesso direto à plataforma"
    },
    {
      icon: <MessageCircle className="w-5 h-5 text-luxury-gold" />,
      text: "Participação ativa com feedbacks"
    },
    {
      icon: <Users className="w-5 h-5 text-luxury-gold" />,
      text: "Canal direto com os criadores"
    },
    {
      icon: <Star className="w-5 h-5 text-luxury-gold" />,
      text: "Vaga garantida no lançamento oficial"
    }
  ];

  return (
    <div className="min-h-screen bg-black relative overflow-hidden font-sans">
      {/* Luxurious Dark Background with Red/Purple Gradients */}
      <div className="absolute inset-0">
        {/* Main dark gradient base */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-luxury-deepPurple/40 to-luxury-red/30"></div>
        
        {/* Additional gradient layers for depth */}
        <div className="absolute inset-0 bg-gradient-to-tr from-black/90 via-transparent to-luxury-darkPink/20"></div>
        <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-luxury-red/10 to-black/80"></div>
        
        {/* Sophisticated blur effects */}
        <div className="absolute top-20 left-10 w-40 h-40 bg-luxury-red/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-luxury-deepPurple/40 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-luxury-darkPink/25 rounded-full blur-3xl"></div>
        
        {/* Additional luxury accent blurs */}
        <div className="absolute top-10 right-20 w-32 h-32 bg-luxury-red/25 rounded-full blur-2xl"></div>
        <div className="absolute bottom-40 left-20 w-44 h-44 bg-luxury-purple/35 rounded-full blur-3xl"></div>
        <div className="absolute top-60 right-40 w-36 h-36 bg-luxury-mediumPink/20 rounded-full blur-2xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 lg:py-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Hero Section */}
          <div className="mb-12 animate-fade-in">
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-sans font-bold text-white mb-6 leading-tight px-2">
              Teste antecipado.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-luxury-gold to-luxury-red">
                Acesso antes de todos.
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed px-4">
              A Interlaced está em fase de protótipo. Ainda não há perfis ativos — mas você pode ser uma das primeiras pessoas a criar o seu e nos ajudar a construir algo único.
            </p>
          </div>

          {/* Benefits Section */}
          <div className="mb-12 animate-fade-in px-2">
            <div className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/20 p-6 sm:p-8 md:p-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-sans font-semibold text-white mb-8">
                Por que participar agora?
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <span className="text-white/90 font-medium text-xs sm:text-sm md:text-base text-left">
                      {benefit.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Call to Action Section */}
          <div className="animate-fade-in px-2">
            <div className="bg-black/50 backdrop-blur-xl rounded-2xl border border-white/30 p-6 sm:p-8 md:p-12">
              <h3 className="text-lg sm:text-xl md:text-2xl font-sans font-semibold text-white mb-8 px-2">
                Quero participar dos testes e ajudar a construir a Interlaced.
              </h3>
              
              {/* Checkbox */}
              <div className="mb-8">
                <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 rounded-xl bg-white/5 border border-white/10 max-w-2xl mx-auto">
                  <Checkbox
                    id="agreement"
                    checked={isAgreed}
                    onCheckedChange={(checked) => setIsAgreed(checked as boolean)}
                    className="mt-1 border-white/30 data-[state=checked]:bg-luxury-gold data-[state=checked]:border-luxury-gold flex-shrink-0"
                  />
                  <label 
                    htmlFor="agreement" 
                    className="text-white/90 text-xs sm:text-sm md:text-base leading-relaxed cursor-pointer text-left"
                  >
                    Estou ciente de que esta é uma versão de testes e estou disposto(a) a colaborar com feedbacks para ajudar no desenvolvimento da plataforma.
                  </label>
                </div>
              </div>

              {/* CTA Button */}
              <div className="w-full max-w-md mx-auto">
                <Button
                  onClick={handleCreateProfile}
                  disabled={!isAgreed}
                  className={`
                    w-full px-4 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-xl transition-all duration-300
                    ${isAgreed 
                      ? 'bg-gradient-to-r from-luxury-gold to-luxury-red hover:from-luxury-gold/90 hover:to-luxury-red/90 text-white shadow-2xl hover:shadow-luxury-gold/25 hover:scale-105' 
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  {isAgreed ? (
                    <span className="flex items-center justify-center gap-2">
                      <span>Criar meu perfil agora</span>
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    </span>
                  ) : (
                    <span>Marque a confirmação acima</span>
                  )}
                </Button>
              </div>

              {isAgreed && (
                <p className="text-white/60 text-xs sm:text-sm mt-4 animate-fade-in px-2">
                  Você será redirecionado para criar sua conta
                </p>
              )}
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-12 text-center animate-fade-in px-4">
            <p className="text-white/50 text-xs sm:text-sm max-w-2xl mx-auto">
              Ao participar dos testes, você está ajudando a moldar o futuro dos relacionamentos de alto padrão. 
              Sua opinião é valiosa e será considerada em cada decisão de produto.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarlyAccess;
